export const OG_SITE_NAME = "Splice";
export const OG_LOCALE = "en_NG";
export const DEFAULT_OG_TITLE = "Book Salons and Spas Instantly | Splice";
export const DEFAULT_OG_DESCRIPTION =
  "Find and book appointments at top-rated salons, spas, and beauty businesses. Easy online booking for hair care, nails, facials, massage, and more.";

const RESERVED_PATH_SEGMENTS = new Set([
  "redirect",
  "favorites",
  "b",
  "pay",
  "receipt",
  "s",
  "assets",
  "fonts",
]);

export type ShareImageKind = "cover" | "logo" | "none";

export type OpenGraphMetadata = {
  title: string;
  description: string;
  documentTitle?: string;
  searchDescription?: string;
  url: string;
  imageUrl: string | null;
  imageAlt: string;
  imageKind: ShareImageKind;
  imageType: string | null;
  siteName: string;
  locale: string;
  type: "website";
  twitterCard: "summary" | "summary_large_image";
  robots?: string;
};

export type BookingPath =
  | { kind: "home" }
  | { kind: "favorites"; shareToken: string }
  | { kind: "appointment" }
  | { kind: "business"; businessSlug: string; branchSlug: string | null }
  | { kind: "other" };

export function parseBookingPath(pathname: string): BookingPath {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return { kind: "home" };

  const [first, second, third] = segments;

  if (first === "favorites" && second) {
    return { kind: "favorites", shareToken: second };
  }

  if (first === "b" || first === "pay" || first === "receipt") {
    return { kind: "appointment" };
  }

  if (first === "s" && second) {
    return {
      kind: "business",
      businessSlug: second,
      branchSlug: third ?? null,
    };
  }

  if (RESERVED_PATH_SEGMENTS.has(first) || first.includes(".")) {
    return { kind: "other" };
  }

  return {
    kind: "business",
    businessSlug: first,
    branchSlug: second ?? null,
  };
}

export function toAbsoluteHttpsUrl(
  url: string | null | undefined,
  origin: string,
): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (/^(?:data|blob|javascript|file):/i.test(trimmed)) return null;

  let resolved = trimmed;
  if (resolved.startsWith("//")) {
    resolved = `https:${resolved}`;
  } else if (resolved.startsWith("/")) {
    resolved = `${origin.replace(/\/$/, "")}${resolved}`;
  } else if (!/^https?:\/\//i.test(resolved)) {
    resolved = `${origin.replace(/\/$/, "")}/${resolved}`;
  }

  if (resolved.startsWith("http://")) {
    resolved = `https://${resolved.slice("http://".length)}`;
  }

  try {
    const parsed = new URL(resolved);
    if (parsed.protocol !== "https:") return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

const UNSHAREABLE_IMAGE_EXTENSION = /\.(?:svg|ico|gif|bmp|tif|tiff)(?:$|\?)/i;

export function isShareableImageUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname.toLowerCase();
    const haystack = `${path}${parsed.search}`.toLowerCase();
    if (UNSHAREABLE_IMAGE_EXTENSION.test(path) || UNSHAREABLE_IMAGE_EXTENSION.test(haystack)) {
      return false;
    }
    if (path.includes("favicon") || haystack.includes("f_svg")) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function shareImageMimeType(url: string | null): string | null {
  if (!url) return null;
  try {
    const path = new URL(url).pathname.toLowerCase();
    if (path.endsWith(".png")) return "image/png";
    if (path.endsWith(".webp")) return "image/webp";
    if (path.endsWith(".jpg") || path.endsWith(".jpeg")) return "image/jpeg";
    return null;
  } catch {
    return null;
  }
}

export function pickShareImage({
  origin,
  headerImages = [],
  headerImageUrl,
  logoUrl,
  productImageUrl,
}: {
  origin: string;
  headerImages?: Array<string | null | undefined>;
  headerImageUrl?: string | null;
  logoUrl?: string | null;
  productImageUrl?: string | null;
}): { url: string | null; kind: ShareImageKind } {
  const coverCandidates = [productImageUrl, ...headerImages, headerImageUrl];
  for (const candidate of coverCandidates) {
    const absolute = toAbsoluteHttpsUrl(candidate, origin);
    if (absolute && isShareableImageUrl(absolute)) {
      return { url: absolute, kind: "cover" };
    }
  }

  const logo = toAbsoluteHttpsUrl(logoUrl, origin);
  if (logo && isShareableImageUrl(logo)) {
    return { url: logo, kind: "logo" };
  }

  return { url: null, kind: "none" };
}

export function truncateOgText(value: string, maxLength: number): string {
  const normalized = value.replace(/\s+/g, " ").trim();
  if (normalized.length <= maxLength) return normalized;
  const sliced = normalized.slice(0, maxLength - 1);
  const lastSpace = sliced.lastIndexOf(" ");
  const cut = lastSpace > maxLength * 0.6 ? sliced.slice(0, lastSpace) : sliced;
  return `${cut.replace(/[.,;:\s]+$/, "")}…`;
}

export function canonicalShareUrl({
  origin,
  pathname,
  serviceId,
  productId,
  bundleId,
}: {
  origin: string;
  pathname: string;
  serviceId?: string | null;
  productId?: string | null;
  bundleId?: string | null;
}): string {
  const url = new URL(pathname || "/", origin);
  url.hash = "";
  url.search = "";
  if (productId) {
    url.searchParams.set("product", productId);
  } else if (serviceId) {
    url.searchParams.set("service", serviceId);
  } else if (bundleId) {
    url.searchParams.set("bundle", bundleId);
  }
  return url.toString();
}

export function buildHomeOpenGraph(origin: string): OpenGraphMetadata {
  return {
    title: DEFAULT_OG_TITLE,
    description: DEFAULT_OG_DESCRIPTION,
    url: `${origin.replace(/\/$/, "")}/`,
    imageUrl: null,
    imageAlt: "Splice online booking",
    imageKind: "none",
    imageType: null,
    siteName: OG_SITE_NAME,
    locale: OG_LOCALE,
    type: "website",
    twitterCard: "summary",
  };
}

export function buildFavoritesOpenGraph({
  origin,
  pathname,
  sharerName,
}: {
  origin: string;
  pathname: string;
  sharerName?: string | null;
}): OpenGraphMetadata {
  return {
    ...buildHomeOpenGraph(origin),
    title: sharerName
      ? truncateOgText(`${sharerName}'s favorite businesses | Splice`, 70)
      : "Shared favorites | Splice",
    description:
      "Browse a curated list of favorite beauty and wellness businesses shared on Splice.",
    url: canonicalShareUrl({ origin, pathname }),
    robots: "noindex, nofollow",
    twitterCard: "summary",
  };
}

export function buildAppointmentOpenGraph({
  origin,
  pathname,
}: {
  origin: string;
  pathname: string;
}): OpenGraphMetadata {
  return {
    ...buildHomeOpenGraph(origin),
    title: "Appointment | Splice",
    description: "View this appointment on Splice.",
    url: canonicalShareUrl({ origin, pathname }),
    robots: "noindex, nofollow",
    twitterCard: "summary",
  };
}

export function formatOgDuration(minutes: number | null | undefined): string | null {
  if (minutes == null || !Number.isFinite(minutes) || minutes <= 0) return null;
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  if (remaining === 0) return hours === 1 ? "1 hr" : `${hours} hrs`;
  return `${hours} hr ${remaining} min`;
}

export function formatOgPrice(
  price: number | null | undefined,
  currencySymbol = "₦",
): string | null {
  if (price == null || !Number.isFinite(price) || price <= 0) return null;
  const hasFraction = price % 1 !== 0;
  return `${currencySymbol}${price.toLocaleString("en-NG", {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: hasFraction ? 2 : 0,
  })}`;
}

export type ShareServiceInput = {
  id?: string | null;
  name?: string | null;
  description?: string | null;
  duration?: number | null;
  price?: number | null;
  pricingType?: string | null;
  isPublic?: boolean | null;
  hasVariants?: boolean | null;
  serviceVariants?: Array<{
    id?: string | null;
    name?: string | null;
    description?: string | null;
    duration?: number | null;
    price?: number | null;
    active?: boolean | null;
  } | null> | null;
};

export type ShareServiceDetails = {
  id: string;
  name: string;
  description: string | null;
  duration: number | null;
  price: number | null;
  pricingType: string | null;
  priceFrom: boolean;
};

function sameShareId(left?: string | null, right?: string | null): boolean {
  return Boolean(left && right && String(left) === String(right));
}

export function resolveShareService(
  services: Array<ShareServiceInput | null | undefined> | null | undefined,
  serviceId?: string | null,
): ShareServiceDetails | null {
  if (!serviceId) return null;

  for (const service of services ?? []) {
    if (!service || service.isPublic === false) continue;
    const variants = (service.serviceVariants ?? []).filter(
      (variant) => variant && variant.active !== false,
    );

    if (sameShareId(service.id, serviceId) && service.name) {
      const prices = [service.price, ...variants.map((variant) => variant?.price)].filter(
        (value): value is number => value != null && Number.isFinite(value) && value > 0,
      );
      const durations = [
        service.duration,
        ...variants.map((variant) => variant?.duration),
      ].filter(
        (value): value is number => value != null && Number.isFinite(value) && value > 0,
      );
      const hasRange = Boolean(service.hasVariants && variants.length > 0);
      return {
        id: String(service.id),
        name: service.name,
        description: service.description?.trim() || null,
        duration: durations.length > 0 ? Math.min(...durations) : service.duration ?? null,
        price: prices.length > 0 ? Math.min(...prices) : service.price ?? null,
        pricingType: service.pricingType ?? null,
        priceFrom: hasRange || service.pricingType === "starting_at",
      };
    }

    const variant = variants.find((item) => sameShareId(item?.id, serviceId));
    if (variant?.name) {
      const parentName = service.name?.trim();
      const variantName = variant.name.trim();
      const name =
        parentName && variantName && parentName !== variantName
          ? `${parentName} (${variantName})`
          : variantName;
      return {
        id: String(variant.id),
        name,
        description: variant.description?.trim() || service.description?.trim() || null,
        duration: variant.duration ?? service.duration ?? null,
        price: variant.price ?? service.price ?? null,
        pricingType: service.pricingType ?? null,
        priceFrom: false,
      };
    }
  }

  return null;
}

export type ShareProductInput = {
  id?: string | null;
  name?: string | null;
  description?: string | null;
  imageUrl?: string | null;
  retailPrice?: number | null;
  listOnBookingSite?: boolean | null;
  isPurchasable?: boolean | null;
};

export type ShareProductDetails = {
  id: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  price: number | null;
};

export function resolveShareProduct(
  products: Array<ShareProductInput | null | undefined> | null | undefined,
  productId?: string | null,
): ShareProductDetails | null {
  if (!productId) return null;

  for (const product of products ?? []) {
    if (!product || product.listOnBookingSite === false) continue;
    if (!sameShareId(product.id, productId) || !product.name) continue;
    return {
      id: String(product.id),
      name: product.name,
      description: product.description?.trim() || null,
      imageUrl: product.imageUrl?.trim() || null,
      price:
        product.retailPrice != null && Number.isFinite(product.retailPrice)
          ? product.retailPrice
          : null,
    };
  }

  return null;
}

export type ShareBundleInput = {
  id?: string | null;
  name?: string | null;
  description?: string | null;
  active?: boolean | null;
  totalPrice?: number | null;
  discountPercentage?: number | null;
  usesCustomPrice?: boolean | null;
  customPrice?: number | null;
  scheduleType?: string | null;
  bundleServices?: Array<{
    quantity?: number | null;
    service?: {
      duration?: number | null;
      extraTime?: number | null;
    } | null;
  } | null> | null;
};

export type ShareBundleDetails = {
  id: string;
  name: string;
  description: string | null;
  price: number | null;
  duration: number | null;
};

/** Bundle a shared booking link points at. Inactive bundles are not public. */
export function resolveShareBundle(
  bundles: Array<ShareBundleInput | null | undefined> | null | undefined,
  bundleId?: string | null,
): ShareBundleDetails | null {
  if (!bundleId) return null;

  for (const bundle of bundles ?? []) {
    if (!bundle || bundle.active === false) continue;
    if (!sameShareId(bundle.id, bundleId) || !bundle.name?.trim()) continue;

    const total = Number(bundle.totalPrice);
    const custom = Number(bundle.customPrice);
    const discount = Number(bundle.discountPercentage);
    let price: number | null = null;
    if (bundle.usesCustomPrice && Number.isFinite(custom) && custom > 0) {
      price = custom;
    } else if (
      Number.isFinite(total) &&
      total > 0 &&
      Number.isFinite(discount) &&
      discount > 0
    ) {
      price = total * (1 - discount / 100);
    } else if (Number.isFinite(total) && total > 0) {
      price = total;
    }

    const minutes = (bundle.bundleServices ?? []).map((row) => {
      const duration = Number(row?.service?.duration) || 0;
      const extra = Number(row?.service?.extraTime) || 0;
      const quantity = Math.max(1, Number(row?.quantity) || 1);
      return (duration + extra) * quantity;
    });
    const schedule = (bundle.scheduleType || "sequence").toLowerCase();
    const duration = minutes.length
      ? schedule === "parallel"
        ? Math.max(...minutes)
        : minutes.reduce((sum, value) => sum + value, 0)
      : null;

    return {
      id: String(bundle.id),
      name: bundle.name.trim(),
      description: bundle.description?.trim() || null,
      price,
      duration: duration && duration > 0 ? duration : null,
    };
  }

  return null;
}

export function buildBusinessOpenGraph({
  origin,
  pathname,
  businessName,
  about,
  locationLabel,
  specialties,
  headerImages,
  headerImageUrl,
  logoUrl,
  serviceName,
  serviceDescription,
  serviceId,
  serviceDuration,
  servicePrice,
  servicePricingType,
  servicePriceFrom,
  productName,
  productDescription,
  productId,
  productPrice,
  productImageUrl,
  bundleName,
  bundleDescription,
  bundleId,
  bundlePrice,
  bundleDuration,
  currencySymbol,
  priceHidden,
}: {
  origin: string;
  pathname: string;
  businessName: string;
  about?: string | null;
  locationLabel?: string | null;
  specialties?: string[] | null;
  headerImages?: Array<string | null | undefined>;
  headerImageUrl?: string | null;
  logoUrl?: string | null;
  serviceName?: string | null;
  serviceDescription?: string | null;
  serviceId?: string | null;
  serviceDuration?: number | null;
  servicePrice?: number | null;
  servicePricingType?: string | null;
  servicePriceFrom?: boolean;
  productName?: string | null;
  productDescription?: string | null;
  productId?: string | null;
  productPrice?: number | null;
  productImageUrl?: string | null;
  bundleName?: string | null;
  bundleDescription?: string | null;
  bundleId?: string | null;
  bundlePrice?: number | null;
  bundleDuration?: number | null;
  currencySymbol?: string | null;
  priceHidden?: boolean | null;
}): OpenGraphMetadata {
  const shareItemName = serviceName || productName || bundleName;
  const image = pickShareImage({
    origin,
    headerImages,
    headerImageUrl,
    logoUrl,
    productImageUrl: serviceName ? null : productImageUrl,
  });

  const location = locationLabel?.trim();
  const title = shareItemName
    ? truncateOgText(`${shareItemName} at ${businessName} | Splice`, 70)
    : truncateOgText(
        location
          ? `Book at ${businessName} in ${location} | Splice`
          : `Book at ${businessName} | Splice`,
        70,
      );

  const specialtyText = (specialties ?? []).filter(Boolean).slice(0, 3).join(", ");
  const durationText = formatOgDuration(
    serviceName ? serviceDuration : bundleName ? bundleDuration : null,
  );
  const listedPrice = serviceName
    ? servicePrice
    : productName
      ? productPrice
      : bundleName
        ? bundlePrice
        : null;
  const priceText =
    priceHidden || !shareItemName
      ? null
      : formatOgPrice(listedPrice, currencySymbol || "₦");
  const priceLabel =
    priceText && (servicePriceFrom || servicePricingType === "starting_at")
      ? `from ${priceText}`
      : priceText;
  const serviceFacts = [durationText, priceLabel].filter(Boolean).join(" · ");

  let description: string;
  if (serviceName) {
    const where = `Book online at ${businessName}${location ? ` in ${location}` : ""}.`;
    const body =
      serviceDescription?.trim() ||
      `Book ${serviceName} at ${businessName}${location ? ` in ${location}` : ""}.`;
    description = [body, serviceFacts, where].filter(Boolean).join(" ");
  } else if (productName) {
    const where = `Shop online at ${businessName}${location ? ` in ${location}` : ""}.`;
    const body =
      productDescription?.trim() ||
      `Shop ${productName} at ${businessName}${location ? ` in ${location}` : ""}.`;
    description = [body, priceLabel, where].filter(Boolean).join(" ");
  } else if (bundleName) {
    const where = `Book this bundle at ${businessName}${location ? ` in ${location}` : ""}.`;
    const body =
      bundleDescription?.trim() ||
      `Book ${bundleName} at ${businessName}${location ? ` in ${location}` : ""}.`;
    const bundleFacts = [durationText, priceLabel].filter(Boolean).join(" · ");
    description = [body, bundleFacts, where].filter(Boolean).join(" ");
  } else {
    description =
      about?.trim() ||
      `Book appointments online at ${businessName}${location ? ` in ${location}` : ""}.`;
    if (specialtyText) {
      description = `${description} ${specialtyText}.`;
    }
    description = `${truncateOgText(description, 180)} Easy online booking.`;
  }

  const searchDescription = shareItemName
    ? truncateOgText(description, 160)
    : truncateOgText(
        about?.trim() || description.replace(/\s*Easy online booking\.?$/, ""),
        160,
      );

  return {
    title,
    description: truncateOgText(description, 200),
    documentTitle: shareItemName
      ? title
      : truncateOgText(
          [businessName, specialtyText || null, location]
            .filter(Boolean)
            .join(" - ") + " | Splice",
          70,
        ),
    searchDescription,
    url: canonicalShareUrl({ origin, pathname, serviceId, productId, bundleId }),
    imageUrl: image.url,
    imageAlt: shareItemName
      ? `${shareItemName} at ${businessName}`
      : `${businessName} booking page`,
    imageKind: image.kind,
    imageType: shareImageMimeType(image.url),
    siteName: OG_SITE_NAME,
    locale: OG_LOCALE,
    type: "website",
    twitterCard: image.kind === "cover" ? "summary_large_image" : "summary",
  };
}

export function escapeHtmlAttribute(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function renderOpenGraphHeadTags(meta: OpenGraphMetadata): string {
  const tags = [
    `<title>${escapeHtmlAttribute(meta.documentTitle ?? meta.title)}</title>`,
    `<meta name="description" content="${escapeHtmlAttribute(meta.searchDescription ?? meta.description)}" />`,
    `<link rel="canonical" href="${escapeHtmlAttribute(meta.url)}" />`,
    `<meta property="og:site_name" content="${escapeHtmlAttribute(meta.siteName)}" />`,
    `<meta property="og:locale" content="${escapeHtmlAttribute(meta.locale)}" />`,
    `<meta property="og:type" content="${escapeHtmlAttribute(meta.type)}" />`,
    `<meta property="og:url" content="${escapeHtmlAttribute(meta.url)}" />`,
    `<meta property="og:title" content="${escapeHtmlAttribute(meta.title)}" />`,
    `<meta property="og:description" content="${escapeHtmlAttribute(meta.description)}" />`,
    `<meta name="twitter:card" content="${meta.twitterCard}" />`,
    `<meta name="twitter:title" content="${escapeHtmlAttribute(meta.title)}" />`,
    `<meta name="twitter:description" content="${escapeHtmlAttribute(meta.description)}" />`,
  ];

  if (meta.robots) {
    tags.splice(3, 0, `<meta name="robots" content="${escapeHtmlAttribute(meta.robots)}" />`);
  }

  if (meta.imageUrl) {
    tags.push(
      `<meta property="og:image" content="${escapeHtmlAttribute(meta.imageUrl)}" />`,
      `<meta property="og:image:secure_url" content="${escapeHtmlAttribute(meta.imageUrl)}" />`,
      `<meta property="og:image:alt" content="${escapeHtmlAttribute(meta.imageAlt)}" />`,
      `<meta name="twitter:image" content="${escapeHtmlAttribute(meta.imageUrl)}" />`,
      `<meta name="twitter:image:alt" content="${escapeHtmlAttribute(meta.imageAlt)}" />`,
    );
    if (meta.imageType) {
      tags.push(
        `<meta property="og:image:type" content="${escapeHtmlAttribute(meta.imageType)}" />`,
      );
    }
    if (meta.imageKind === "cover") {
      tags.push(
        `<meta property="og:image:width" content="1200" />`,
        `<meta property="og:image:height" content="630" />`,
      );
    }
  }

  return tags.join("\n    ");
}

export function injectOpenGraphHeadTags(html: string, meta: OpenGraphMetadata): string {
  const withoutExisting = html
    .replace(/<title>[\s\S]*?<\/title>/i, "")
    .replace(
      /<meta\s+[^>]*(?:property|name)="(?:og:[^"]+|twitter:[^"]+|description|robots)"[^>]*>\s*/gi,
      "",
    )
    .replace(/<link\s+[^>]*rel="canonical"[^>]*>\s*/gi, "");

  return withoutExisting.replace(
    /<\/head>/i,
    `    ${renderOpenGraphHeadTags(meta)}\n  </head>`,
  );
}
